/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
} from "@material-ui/core";
import {
  array as arrayPropType,
  bool,
  object as objectPropType,
} from "prop-types";
import EditCard from "../EditCard";
import {
  EDIT_PROFILE,
  GET_STATES,
  PROFILE_LOADING,
  STOP_PROFILE_LOADING,
} from "../../../../../redux/actionTypes";
import httpRequest from "../../../../../ajax";
import {
  ternaryResolver,
  isValidE164PhoneNumber,
  uploadFile,
} from "../../../../../utils/helpers";
import { getStateAction } from "../../../../../redux/actions/stateActions";
import { updateProfileAction } from "../../../../../redux/actions/profileActions";
import SearchLocationInput from "../../../../../components/LocationComponent";
import cogoToast from "cogo-toast";
import parseResume from "../../../../../utils/resumeParser";
import { parseResumeWithAI } from "../../../../../utils/aiResumeParser";
import ResumePreviewModal from "../Resume/ResumePreviewModal";
import "./style.css";

const useStyles = makeStyles((theme) => ({
  inputField: {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
      backgroundColor: "rgba(58, 110, 212, 0.08)", // A light, transparent blue
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiSelect-select": {
      backgroundColor: "rgba(58, 110, 212, 0.08)", // A light, transparent blue for Select
    },
  },
}));
const BasicInfo = ({
  // industries,
  profileData,
  states,
  countries,
  profileLoading,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [isOnEditLocation, setIsOnEditLocation] = useState(false);

  // Phone validation state
  const [phoneError, setPhoneError] = useState("");
  // Phone verification states
  // const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Resume parsing states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const parseResumeInputRef = React.useRef(null);

  // Determine if the current phone matches the saved phone in storage
  const getStoredUserData = () => {
    try {
      return (
        JSON.parse(sessionStorage.getItem("userData")) ||
        JSON.parse(localStorage.getItem("userData")) ||
        {}
      );
    } catch (e) {
      return {};
    }
  };
  const storedUserData = getStoredUserData();
  const isPhoneSavedAndMatching =
    !!storedUserData.phone && storedUserData.phone === profileData.phone;

  const handleChange = ({ target: { name, value } }) => {
    if (name === "country") {
      dispatch({ type: GET_STATES, payload: [] });
      const selectedCountry = countries.find(({ id }) => value === id);
      dispatch({ type: EDIT_PROFILE, payload: { name, value } });
      if (selectedCountry) {
        getStateAction(selectedCountry.id, dispatch);
      }
    } else if (name === "phone") {
      // Validate phone number in E.164 format
      if (value && !isValidE164PhoneNumber(value)) {
        setPhoneError(
          "Phone number must be in E.164 format (e.g., +1234567890)"
        );
      } else {
        setPhoneError("");
      }
      // If phone number changes, mark verification as not verified
      dispatch({ type: EDIT_PROFILE, payload: { name, value } });
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "is_phone_verified", value: 0 },
      });
    } else {
      dispatch({ type: EDIT_PROFILE, payload: { name, value } });
    }
  };

  const handleLocationChange = (place) => {
    dispatch({
      type: EDIT_PROFILE,
      payload: { name: "location", value: place },
    });
  };

  // Send OTP for phone verification
  const handleSendOtp = async () => {
    // Require that the phone is saved first (user must click Update)
    if (!isPhoneSavedAndMatching) {
      cogoToast.error(
        "Please click Update to save your phone number before verifying",
        {
          hideAfter: 5,
          position: "top-center",
        }
      );
      return;
    }

    if (!profileData.phone || !profileData.country) {
      cogoToast.error("Please enter both phone number and country", {
        hideAfter: 4,
        position: "top-center",
      });
      return;
    }

    // Validate E.164 format
    if (!isValidE164PhoneNumber(profileData.phone)) {
      cogoToast.error(
        "Please enter a valid phone number in E.164 format (e.g., +1234567890)",
        {
          hideAfter: 4,
          position: "top-center",
        }
      );
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await httpRequest(
        "post",
        "/settings/profile/send-phone-verification-otp",
        {
          phone: profileData.phone,
          country: profileData.country,
        }
      );

      if (response.data.status) {
        setOtpModalOpen(true);
      }
    } catch (error) {
      cogoToast.error(error?.response?.data?.message || "Failed to send OTP", {
        hideAfter: 4,
        position: "top-center",
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      cogoToast.error("Please enter a valid 6-digit OTP", {
        hideAfter: 4,
        position: "top-center",
      });
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await httpRequest(
        "post",
        "/settings/profile/verify-phone-otp",
        {
          phone: profileData.phone,
          country: profileData.country,
          otp: otp,
        }
      );
      // response received
      if (response.data.status) {
        // Update Redux profile state so UI reflects verification immediately
        dispatch({
          type: EDIT_PROFILE,
          payload: { name: "is_phone_verified", value: 1 },
        });

        // Also persist update to session/local storage so other parts of the app read the updated value
        try {
          const stored = JSON.parse(
            sessionStorage.getItem("userData") ||
              localStorage.getItem("userData") ||
              "{}"
          );
          if (stored && typeof stored === "object") {
            stored.is_phone_verified = 1;
            // some backends may use phone_verified as a key; keep both in sync
            stored.phone_verified = 1;
            try {
              sessionStorage.setItem("userData", JSON.stringify(stored));
            } catch (e) {
              // ignore session storage write errors
            }
            try {
              localStorage.setItem("userData", JSON.stringify(stored));
            } catch (e) {
              // ignore local storage write errors
            }
          }
        } catch (e) {
          // ignore JSON parse errors
        }

        setOtpModalOpen(false);
        setOtp("");
        // Toast is shown automatically by axios interceptor
      }
    } catch (error) {
      cogoToast.error(
        error?.response?.data?.message || "Failed to verify OTP",
        {
          hideAfter: 4,
          position: "top-center",
        }
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle resume file upload and parsing
  const handleParseResumeClick = () => {
    parseResumeInputRef.current.click();
  };

  const handleResumeFileParse = async ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        cogoToast.error("Please upload a PDF or DOCX file", {
          hideAfter: 4,
          position: "top-center",
        });
        return;
      }

      setIsParsing(true);
      setPreviewOpen(true);

      // Upload the resume file to Cloudinary
      try {
        const uploadResult = await uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        );

        if (uploadResult.success) {
          // Store the uploaded resume URL and name in session storage
          sessionStorage.setItem("uploadedResumeUrl", uploadResult.data);
          sessionStorage.setItem("uploadedResumeName", file.name);
        }
      } catch (uploadError) {
        cogoToast.error("Failed to upload resume file", {
          hideAfter: 4,
          position: "top-center",
        });
      }

      // Parse the resume with AI (fallback to regex if AI fails)
      try {
        console.log("Attempting AI-based resume parsing...");
        const data = await parseResumeWithAI(file);
        console.log("AI parsed resume data:", data);
        console.log("About/Summary:", data.about);
        setParsedData(data);
        cogoToast.success("Resume parsed with AI successfully! 🤖✨", {
          hideAfter: 3,
          position: "top-center",
        });
      } catch (aiError) {
        console.warn(
          "AI parsing failed, falling back to regex parser:",
          aiError
        );

        // Fallback to regex parser
        try {
          const data = await parseResume(file);
          console.log("Regex parsed resume data:", data);
          console.log("About/Summary:", data.about);
          setParsedData(data);
          cogoToast.info("Resume parsed successfully (basic mode)", {
            hideAfter: 3,
            position: "top-center",
          });
        } catch (regexError) {
          cogoToast.error(regexError.message || "Failed to parse resume", {
            hideAfter: 4,
            position: "top-center",
          });
          setPreviewOpen(false);
        }
      } finally {
        setIsParsing(false);
        // Reset file input
        parseResumeInputRef.current.value = "";
      }
    }
  };

  const handleApplyParsedData = (data) => {
    // Apply extracted data to form fields
    if (data.first_name) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "first_name", value: data.first_name },
      });
    }
    if (data.last_name) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "last_name", value: data.last_name },
      });
    }
    if (data.phone) {
      // Validate phone before applying
      if (isValidE164PhoneNumber(data.phone)) {
        dispatch({
          type: EDIT_PROFILE,
          payload: { name: "phone", value: data.phone },
        });
      }
    }

    // Store full parsed data in session storage for other sections to use
    sessionStorage.setItem("parsedResumeData", JSON.stringify(data));

    // Get the uploaded resume URL and name from sessionStorage
    const uploadedResumeUrl = sessionStorage.getItem("uploadedResumeUrl");
    const uploadedResumeName = sessionStorage.getItem("uploadedResumeName");

    // Update the user's profile with the resume
    if (uploadedResumeUrl && uploadedResumeName) {
      const payload = {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        gender: profileData.gender,
        dob: profileData.dob,
        state: profileData.state,
        country: profileData.country,
        industry: profileData.industry,
        image_url: profileData.image_url,
        resume: uploadedResumeUrl,
        resumeName: uploadedResumeName,
        phone: profileData.phone,
        links: profileData.links,
        isShowOnProfile: 1,
        about: profileData.about,
      };

      dispatch({ type: PROFILE_LOADING });

      updateProfileAction(payload, dispatch)
        .then(() => {
          cogoToast.success(
            "Resume and basic info applied to profile successfully!",
            {
              hideAfter: 6,
              position: "top-center",
            }
          );
        })
        .catch((error) => {
          cogoToast.error(
            error?.response?.data?.message || "Failed to update resume",
            {
              hideAfter: 4,
              position: "top-center",
            }
          );
        })
        .finally(() => {
          dispatch({ type: STOP_PROFILE_LOADING });
        });
    } else {
      cogoToast.success(
        "Basic info populated! Navigate to other sections (About, Education, Experience) to apply additional data.",
        {
          hideAfter: 6,
          position: "top-center",
        }
      );
    }

    setPreviewOpen(false);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setParsedData(null);
  };

  const handleSubmit = () => {
    // Validate phone number if it's provided
    if (profileData.phone && !isValidE164PhoneNumber(profileData.phone)) {
      cogoToast.error(
        "Please enter a valid phone number in E.164 format (e.g., +1234567890)",
        {
          hideAfter: 4,
          position: "top-center",
        }
      );
      return;
    }

    const originalData = JSON.parse(
      sessionStorage.getItem("userData") ||
        localStorage.getItem("userData") ||
        "{}"
    );

    const payload = {
      first_name: profileData.first_name,
      last_name: profileData.last_name,
      title: profileData.title,
      gender: profileData.gender,
      dob: profileData.dob,
      state: profileData.state,
      country: profileData.country,
      industry: profileData.industry,
      image_url: profileData.image_url,
      resume: profileData.resume,
      links: profileData.links,
      isShowOnProfile: 1,
      about: profileData.about,
      location: profileData.location || "",
    };

    // Include phone only if changed
    if (originalData.phone !== profileData.phone || !originalData.phone) {
      payload.phone = profileData.phone;
    }

    dispatch({ type: PROFILE_LOADING });

    updateProfileAction(payload, dispatch)
      .catch((error) => {
        // Extract a single error message
        const errorMessage =
          error?.payload ||
          error?.response?.data?.message ||
          "Error updating profile";

        if (
          errorMessage.includes("OTP") ||
          errorMessage.includes("verification")
        ) {
          cogoToast.error(
            "Phone verification required. Please verify your phone number first.",
            {
              hideAfter: 6,
              position: "top-center",
            }
          );
        } else {
          cogoToast.error(errorMessage, {
            hideAfter: 4,
            position: "top-center",
          });
        }
      })
      .finally(() => {
        dispatch({ type: STOP_PROFILE_LOADING });
      });
  };

  useEffect(() => {
    const storedTitle = localStorage.getItem("title");
    if (storedTitle && storedTitle !== profileData.title) {
      dispatch({
        type: EDIT_PROFILE,
        payload: { name: "title", value: storedTitle },
      });
    }
  }, [dispatch]);

  // Add useEffect to initialize phone verification status from profileData
  useEffect(() => {
    // You might want to check if phone is verified from profileData
    // setIsPhoneVerified(profileData.phone_verified || false);
  }, [profileData.phone]);

  return (
    <>
      <EditCard
        handleSubmit={handleSubmit}
        allRequired
        loading={profileLoading}
        btnText="Update"
        disable={[
          !profileData.first_name,
          !profileData.last_name,
          !profileData.title,
          !profileData.phone,
          !profileData.country,
          !profileData.state,
          !profileData.location,
          !profileData.dob,
          !profileData.gender,
          !!phoneError,
        ].includes(true)}
      >
        {/* Pull from Resume Section */}
        <div
          className="w-100 mb-4"
          style={{ paddingBottom: "20px", borderBottom: "1px solid #e0e0e0" }}
        >
          <h6 style={{ marginBottom: "8px" }}>Quick Fill from Resume</h6>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>
            Upload your resume to automatically populate your basic information
          </p>
          <input
            ref={parseResumeInputRef}
            type="file"
            onChange={handleResumeFileParse}
            style={{ display: "none" }}
            accept="application/pdf, .docx"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleParseResumeClick}
            disabled={isParsing}
            style={{ backgroundColor: "#28a745", marginBottom: "8px" }}
          >
            {isParsing ? "Parsing..." : "📄 Auto-fill from Resume"}
          </Button>
        </div>

        {/* Form Fields */}
        <TextField
          onChange={handleChange}
          name="first_name"
          value={profileData.first_name || ""}
          className="w-100 mb-4"
          variant="outlined"
          label="First name"
        />
        <TextField
          onChange={handleChange}
          name="last_name"
          value={profileData.last_name || ""}
          className="w-100 mb-4"
          variant="outlined"
          label="Last name"
        />
        <TextField
          onChange={handleChange}
          name="title"
          value={profileData.title || ""}
          className="w-100 mb-4"
          variant="outlined"
          label="Title"
          helperText="E.g Software Developer or Customer Service Rep."
        />
        <TextField
          name="email"
          disabled
          value={profileData.email || ""}
          className="w-100 mb-4"
          variant="outlined"
          label={ternaryResolver(profileData.email, "", "Email Address")}
        />

        <FormControl
          variant="outlined"
          className={`w-100 mb-4 ${classes.inputField}`}
        >
          <InputLabel name="country-label">Country</InputLabel>
          <Select
            labelId="country-label"
            name="country"
            label="country"
            value={profileData.country || ""}
            onChange={handleChange}
          >
            <MenuItem value="">Select a country</MenuItem>
            {countries.map(({ name, id }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          className={`w-100 mb-4 ${classes.inputField}`}
        >
          <InputLabel name="state-label">State</InputLabel>
          <Select
            labelId="state-label"
            name="state"
            label="state"
            value={profileData.state || ""}
            onChange={handleChange}
            disabled={states.length === 0}
          >
            <MenuItem value="">Select state</MenuItem>
            {states.map(({ name, id }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isOnEditLocation ? (
          <FormControl
            variant="outlined"
            className={`w-100 mb-4 ${classes.inputField}`}
          >
            <SearchLocationInput
              inputStyle="TextField"
              value={profileData.location || ""}
              handleLocationChange={handleLocationChange}
            />
          </FormControl>
        ) : (
          <>
            <span
              onClick={() => setIsOnEditLocation(true)}
              style={{ marginLeft: "80%", cursor: "pointer" }}
            >
              Change Location
            </span>
            <TextField
              onChange={handleChange}
              value={profileData.location || ""}
              name="location"
              className="w-100 mb-4"
              variant="outlined"
              label="Location"
            />
          </>
        )}
        {/* Phone Number with Verification Status */}
        <div className="w-100 mb-4">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
              Phone Number
            </span>
            <span
              style={{
                marginLeft: "10px",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                backgroundColor:
                  profileData.is_phone_verified == 1 ? "#4caf50" : "#f44336",
                color: "white",
              }}
            >
              {profileData.is_phone_verified == 1 ? "Verified" : "Not Verified"}
            </span>
            {profileData.is_phone_verified == 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleSendOtp}
                disabled={
                  isSendingOtp ||
                  !profileData.phone ||
                  !profileData.country ||
                  !!phoneError ||
                  !isPhoneSavedAndMatching
                }
                style={{ marginLeft: "10px" }}
              >
                {isSendingOtp ? "Sending..." : "Verify Phone"}
              </Button>
            )}
          </div>
          <TextField
            onChange={handleChange}
            value={profileData.phone || ""}
            name="phone"
            className="w-100"
            variant="outlined"
            label="Phone Number"
            error={!!phoneError}
            helperText={
              phoneError ||
              "Enter phone number in E.164 format (e.g., +1234567890)"
            }
            placeholder="+1234567890"
          />
        </div>

        <TextField
          InputLabelProps={{
            shrink: true,
          }}
          onChange={handleChange}
          value={profileData.dob || ""}
          name="dob"
          type="date"
          className="w-100 mb-4"
          variant="outlined"
          label="Date of birth"
        />

        <FormControl
          variant="outlined"
          className={`w-100 mb-4 ${classes.inputField}`}
        >
          <InputLabel name="gender-label">Gender</InputLabel>
          <Select
            labelId="gender-label"
            name="gender"
            label="gender"
            onChange={handleChange}
            value={profileData.gender || ""}
          >
            <MenuItem value="">Select a gender</MenuItem>
            <MenuItem value="2">Female</MenuItem>
            <MenuItem value="1">Male</MenuItem>
            <MenuItem value="3">Others</MenuItem>
          </Select>
        </FormControl>
      </EditCard>

      {/* OTP Verification Modal */}
      <Dialog
        open={otpModalOpen}
        onClose={() => !isVerifyingOtp && setOtpModalOpen(false)}
      >
        <DialogTitle>Verify Phone Number</DialogTitle>
        <DialogContent>
          <p>
            Please enter the 6-digit OTP sent to your phone number:{" "}
            {profileData.phone}
          </p>
          <TextField
            autoFocus
            margin="dense"
            label="Enter OTP"
            type="text"
            fullWidth
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            inputProps={{
              maxLength: 6,
              style: {
                padding: "14px 16px",
                borderRadius: "12px",
                textAlign: "center",
                fontSize: "18px",
                letterSpacing: "8px",
              },
            }}
            InputProps={{
              style: {
                borderRadius: "12px",
              },
            }}
            variant="outlined"
            disabled={isVerifyingOtp}
            placeholder="123456"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOtpModalOpen(false)}
            disabled={isVerifyingOtp}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyOtp}
            color="primary"
            disabled={isVerifyingOtp || !otp || otp.length !== 6}
          >
            {isVerifyingOtp ? "Verifying..." : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Preview Modal */}
      <ResumePreviewModal
        open={previewOpen}
        handleClose={handleClosePreview}
        parsedData={parsedData}
        onApply={handleApplyParsedData}
        isLoading={isParsing}
      />
    </>
  );
};

BasicInfo.propTypes = {
  // industries: arrayPropType.isRequired,
  countries: arrayPropType.isRequired,
  states: arrayPropType.isRequired,
  profileData: objectPropType.isRequired,
  profileLoading: bool.isRequired,
};

export default BasicInfo;
